"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/format";
import { Container } from "../layout/Container";

export type PackageNavItem = { id: string; label: string; icon: string };

type Props = {
  items: PackageNavItem[];
  title: string;
  rating?: number;
  reviewsCount?: number;
  /** id of the element the "pinned" compact title row should appear once
   *  scrolled past — production's real #navbar-sticky-container (the
   *  breadcrumb+title block), not the gallery below it. */
  sentinelTargetId: string;
};

/**
 * Sticky in-page section nav matching production's real markup exactly
 * (confirmed via the live DOM): a scroll-spy nav bar that is visible from
 * page load (not hidden until scroll like the previous build), plain text
 * links with a bottom-border underline indicator on the active section
 * (not a filled pill), real sprite icons, and a compact title+rating row
 * that only appears once the page-title block scrolls out of view.
 */
export function PackageNav({
  items,
  title,
  rating,
  reviewsCount,
  sentinelTargetId,
}: Props) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string>(items[0]?.id ?? "");
  const [pinned, setPinned] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel goes above viewport top, show sticky navigation
        setVisible(entry.boundingClientRect.top < 0);
      },
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sentinel = document.getElementById(sentinelTargetId);
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPinned(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelTargetId]);

  useEffect(() => {
    if (!items.length) return;
    const observed = items
      .map((it) => document.getElementById(it.id))
      .filter((el): el is HTMLElement => el !== null);
    if (!observed.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    observed.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  if (items.length < 2) return null;

  return (
    <>
      <div
        ref={sentinelRef}
        className="h-px w-full absolute -mt-px pointer-events-none"
      />
      <div
        className={cn(
          "fixed top-0 left-0 right-0 z-30 overflow-hidden shadow-sm bg-paper transition-all duration-300 ease-in-out transform",
          visible
            ? "translate-y-0 opacity-100"
            : "-translate-y-20 opacity-0 pointer-events-none",
        )}
      >
        <Container>
          {pinned ? (
            <div className="hidden items-center pt-3 lg:flex">
              <h2 className="font-extrabold text-headings md:text-xl">
                {title}
              </h2>
              {typeof rating === "number" && rating > 0 ? (
                <div className="ml-auto inline-flex flex-wrap text-right items-center justify-end leading-[20px]">
                  <i className="ratings__5 scale-90"></i>
                  <b className="rounded-full bg-secondary px-2 py-[1px] text-xxs leading-[1] font-extrabold text-white">
                    {rating.toFixed(1)}
                  </b>
                  {reviewsCount ? (
                    <a
                      href="#reviews"
                      className="block flex-[0_0_100%] text-xxs font-bold text-headings/70 underline"
                    >
                      Based on {reviewsCount} reviews
                    </a>
                  ) : null}
                </div>
              ) : null}
              <a
                href="#package-options"
                className="ml-3 rounded-md border border-primary bg-primary px-4 py-2 text-xxs font-extrabold text-white"
              >
                See Options
              </a>
            </div>
          ) : null}

          <nav>
            <ul className="nav-wrapper flex items-center gap-x-6 overflow-x-scroll whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {items.map((it) => {
                const isActive = active === it.id;
                return (
                  <li key={it.id}>
                    <a
                      href={`#${it.id}`}
                      className={cn(
                        "relative flex items-center gap-x-1 py-3 text-xs font-bold before:absolute before:inset-x-0 before:bottom-0 before:h-[2px] before:bg-primary text-headings lg:pt-1.5 lg:pb-3 md:text-sm",
                        isActive
                          ? "opacity-100 before:opacity-100"
                          : "opacity-70 before:opacity-0 hover:opacity-100",
                      )}
                    >
                      <svg className="h-5 w-5 text-primary" aria-hidden>
                        <use xlinkHref={`/icons.svg#${it.icon}`} />
                      </svg>
                      {it.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </Container>
      </div>
    </>
  );
}
